import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import { Box, ClickAwayListener, MenuItem } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles, { CardHeader } from 'features/setting/break-management/style'

/** API */
import BreakTemplateApi, { BreakTemplateInterface } from 'api/setting/break-management/break-template.api'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import PageModal from 'new-components/modal/page-modal'
import TextInput from 'new-components/inputs/text-input'
import TextAreaInput from 'new-components/inputs/text-area-input'
import SwitchButton from 'new-components/buttons/switch-button'
import { ColorTag } from 'features/setting/tag/style'
import FormManageColor from 'features/setting/tag/form-manage-color'
import { Form } from 'react-bootstrap'

const initStateErrorMessage = {
  MESSAGE_REQUIRED: '',
  IS_DUPLICATE_BREAK_TEMPLATE: ''
}

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function BreakTemplate() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [breakTemplates, setBreakTemplates] = useState([])
  const [breakTemplateId, setBreakTemplateId] = useState(0)
  const [message, setMessage] = useState('')
  const [messageEn, setMessageEn] = useState('')
  const [color, setColor] = useState('#1CB99A')
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [breakTemplateStatus, setBreakTemplateStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [alertId, setAlertId] = useState<number>(0)
  const [alertName, setAlertName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>('success')
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await BreakTemplateApi.findAll(condition)
    console.log('break-templates response:', res.status, res.data)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setBreakTemplates(Array.isArray(res.data) ? res.data : [])
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const validate = () => {
    if (!message) {
      setErrorMessage({
        ...initStateErrorMessage,
        MESSAGE_REQUIRED: !message ? t('BREAK_TEMPLATE.MESSAGE.MESSAGE_REQUIRED') : ''
      })
      return false
    }
    return true
  }

  const submit = () => {
    if (!validate()) return

    const payload: BreakTemplateInterface = {
      message,
      messageEn,
      color,
      status: 'ACTIVE'
    }

    dispatch(submitModal())

    BreakTemplateApi.create(payload)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('BREAK_TEMPLATE.MESSAGE.SUCCESS.CREATE'))
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...initStateErrorMessage, [err.message]: t(`BREAK_TEMPLATE.MESSAGE.${err.message}`) })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...initStateErrorMessage, [err.message]: t(`BREAK_TEMPLATE.MESSAGE.${err.message}`) })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!validate()) return

    const payload: BreakTemplateInterface = {
      message,
      messageEn,
      color,
      status: breakTemplateStatus
    }

    dispatch(submitModal())

    BreakTemplateApi.update(breakTemplateId, payload)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('BREAK_TEMPLATE.MESSAGE.SUCCESS.UPDATE'))
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...initStateErrorMessage, [err.message]: t(`BREAK_TEMPLATE.MESSAGE.${err.message}`) })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...initStateErrorMessage, [err.message]: t(`BREAK_TEMPLATE.MESSAGE.${err.message}`) })
        dispatch(unSubmitModal())
      })
  }

  const resetForm = () => {
    setBreakTemplateId(0)
    setMessage('')
    setMessageEn('')
    setBreakTemplateStatus('')
    setColor('#1CB99A')
    setShowColorPicker(false)
    setIsPopup(false)
    setErrorMessage(initStateErrorMessage)
  }

  const onActive = () => {
    if (alertId) {
      BreakTemplateApi.updateStatus(alertId, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('BREAK_TEMPLATE.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('error')
          setLabelSnack(t('BREAK_TEMPLATE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (alertId) {
      BreakTemplateApi.remove(alertId).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('success')
          setLabelSnack(t('BREAK_TEMPLATE.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack('error')
          setLabelSnack(t('BREAK_TEMPLATE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { breakTemplateId: id, message: msg, messageEn: msgEn, color: c, status: s } = data
    setBreakTemplateId(id)
    setMessage(msg)
    setMessageEn(msgEn || '')
    setColor(c || '#1CB99A')
    setBreakTemplateStatus(s)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const handleSubmitModal = async (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setAlertId(id)
    setAlertName(obj.message)

    if (status === 'DELETE') {
      const res = await BreakTemplateApi.checkUsage(id).catch(() => ({ data: { count: 0 } }))
      const count = res?.data?.count || 0
      if (count > 0) {
        setAlertName(`${obj.message} (${t('BREAK_TEMPLATE.ALERT.IN_USE', { count })})`)
      }
    }

    setIsOpenAlertModal(true)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'color', disablePadding: false, label: t('BREAK_TEMPLATE.TABLE.COLOR') },
    { id: 'message', disablePadding: false, label: t('BREAK_TEMPLATE.TABLE.MESSAGE'), align: 'left' },
    { id: 'name', disablePadding: false, label: t('BREAK_TEMPLATE.TABLE.MESSAGE_EN'), align: 'left' },
    { id: 'status', disablePadding: false, label: t('BREAK_TEMPLATE.TABLE.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('BREAK_TEMPLATE.TABLE.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { breakTemplateId: id, message: msg, messageEn: msgEn, color: c, isDefault: def, updatedBy, updatedAt, status: s } = objData
    const isDefaultTemplate = def === 1
    const statusBtnActive = isDefaultTemplate || s === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = isDefaultTemplate || s === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: id,
      id: id,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: (<div style={{ width: 75, height: 25, backgroundColor: c || '#1CB99A' }}></div>) },
        { option: 'TEXT', align: 'left', label: msg },
        { option: 'TEXT', align: 'left', label: msgEn },
        { option: 'STATUS', align: 'center', label: s },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy || '-', updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t('STATUS.ACTIVE'), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t('STATUS.INACTIVE'), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t('BUTTON.EDIT'), disabled: isDefaultTemplate || permissions.isUpdate.disabled },
            { option: 'DELETE', label: t('BUTTON.DELETE'), disabled: isDefaultTemplate || permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`BREAK_TEMPLATE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`BREAK_TEMPLATE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE' : 'CONFIRM_STATUS'}`, { name: alertName, status: t(`BREAK_TEMPLATE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <CardHeader className="border-0 h-100">
        <HeaderCard text={t('BREAK_TEMPLATE.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('BREAK_TEMPLATE.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={() => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsPopup(true)} textButton={t('BREAK_TEMPLATE.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={breakTemplates.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </CardHeader>

      <PageModal
        title={breakTemplateId ? t('BREAK_TEMPLATE.TITLE_UPDATE') : t('BREAK_TEMPLATE.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => (breakTemplateId ? submitEdit() : submit()) }}
        handleClose={resetForm}
        size={'XS'}
        overflow
        disableBackdropClick
      >
        <Box className="py-2">
          {breakTemplateId ? (
            <Form className="mx-0 mt-0 mb-3 p-0 d-inline-flex w-100">
              <Form.Label className="mr-auto">{t('BREAK_TEMPLATE.FORM.STATUS')}</Form.Label>
              <SwitchButton
                onChange={() => setBreakTemplateStatus(breakTemplateStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                checked={breakTemplateStatus === 'ACTIVE' ? true : false}
              />
            </Form>
          ) : (
            ''
          )}

          <Col>
            <TextInput
              required
              fullWidth
              label={t('BREAK_TEMPLATE.FORM.MESSAGE')}
              value={message}
              helperText={errorMessage.MESSAGE_REQUIRED || errorMessage.IS_DUPLICATE_BREAK_TEMPLATE}
              onChange={(e) => {
                setMessage(e.target.value)
                setErrorMessage(initStateErrorMessage)
              }}
            />
          </Col>
          <Col className="mt-3">
            <TextInput
              fullWidth
              label={t('BREAK_TEMPLATE.FORM.MESSAGE_EN')}
              value={messageEn}
              onChange={(e) => {
                setMessageEn(e.target.value)
                setErrorMessage(initStateErrorMessage)
              }}
            />
          </Col>
          <Col className="mt-3">
            <Box sx={{ fontSize: '14px', marginBottom: '4px' }}>{t('BREAK_TEMPLATE.FORM.COLOR')}</Box>
            <ClickAwayListener onClickAway={() => setShowColorPicker(false)}>
              <Box sx={{ position: 'relative', zIndex: 1300 }}>
                <ColorTag
                  color={color || 'transparent'}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                />
                {showColorPicker && (
                  <FormManageColor
                    color={color}
                    setColor={setColor}
                    handleChangeColor={({ hex }: { hex: string }) => setColor(hex)}
                    onLoadOperativeType={() => undefined}
                    operativeTypes={[]}
                    operativeType={null}
                    handleChangeOperative={() => undefined}
                    disabledTextInput
                  />
                )}
              </Box>
            </ClickAwayListener>
          </Col>
        </Box>
      </PageModal>
    </div>
  )
}
